/**
 * Archive & Records — shared types + display maps (drafts/57 P3).
 * The archive is a LENS over the controlled-document backbone: each record
 * carries an intake cover sheet, a risk-based approval gate, and its own
 * retention / legal-hold / destruction lifecycle.
 */
export type ArchiveStatus =
  | 'PENDING_APPROVAL' | 'ACTIVE' | 'PENDING_DESTRUCTION' | 'DESTROYED' | 'REJECTED';
export type RiskClass = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type Confidentiality = 'PUBLIC' | 'INTERNAL' | 'CONFIDENTIAL' | 'RESTRICTED';
export type StorageMedium = 'ELECTRONIC' | 'PHYSICAL' | 'HYBRID';

export interface ArchiveRecord {
  archive_record_id: string;
  document_id: string | null;
  document_number: string | null;
  title: string;
  record_type: string;
  department_id: string | null;
  department_name: string | null;
  site_id: string | null;
  site_name: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  archived_date: string | null;
  confidentiality: Confidentiality;
  archive_reason: string | null;
  source_system: string | null;
  storage_medium: StorageMedium;
  storage_location: string | null;
  risk_class: RiskClass;
  required_approver_role: string;
  approved_by_user_id: string | null;
  approved_by_name: string | null;
  approved_at_utc: string | null;
  rejection_reason: string | null;
  status: ArchiveStatus;
  retention_policy_id: string | null;
  retention_policy_name: string | null;
  retention_end_at: string | null;
  legal_hold_active: boolean;
  legal_hold_ref: string | null;
  destruction_scheduled_at: string | null;
  destroyed_at_utc: string | null;
  content_hash_at_destroy: string | null;
  created_by_user_id: string;
  created_at_utc: string;
  updated_at_utc: string;
}

export interface ArchiveDashboardData {
  summary: {
    total: number; active: number; pending_approval: number;
    pending_destruction: number; destroyed: number; legal_hold: number;
    destruction_due_90: number;
  };
  by_type: { record_type: string; n: number }[];
  by_confidentiality: { confidentiality: string; n: number }[];
  destruction_due: ArchiveRecord[];
}

export interface ArchiveRecordsResponse { items: ArchiveRecord[]; total: number }

export const ARCHIVE_RECORD_TYPES = [
  'BMR', 'SOP', 'TEST_METHOD', 'VALIDATION', 'QC_DATA', 'TRAINING', 'CAPA', 'GDP_PROC', 'OTHER',
] as const;
export const RISK_CLASSES: RiskClass[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const CONFIDENTIALITIES: Confidentiality[] = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
export const STORAGE_MEDIA: StorageMedium[] = ['ELECTRONIC', 'PHYSICAL', 'HYBRID'];
export const ARCHIVE_STATUSES: ArchiveStatus[] = ['PENDING_APPROVAL', 'ACTIVE', 'PENDING_DESTRUCTION', 'DESTROYED', 'REJECTED'];

// Risk-based approval matrix — mirrors the server-side derivation (ARC-02).
export const APPROVER_BY_RISK: Record<RiskClass, string> = {
  LOW: 'DOC_CONTROLLER', MEDIUM: 'QA_OFFICER', HIGH: 'QA_MANAGER', CRITICAL: 'QA_DIRECTOR',
};

export const STATUS_STYLE: Record<ArchiveStatus, { bg: string; fg: string; label: string }> = {
  PENDING_APPROVAL:    { bg: '#FEF3C7', fg: '#92400E', label: 'Pending approval' },
  ACTIVE:              { bg: '#DCFCE7', fg: '#0E7C5A', label: 'Active' },
  PENDING_DESTRUCTION: { bg: '#FFEDD5', fg: '#9A3412', label: 'Pending destruction' },
  DESTROYED:           { bg: '#E5E7EB', fg: '#374151', label: 'Destroyed' },
  REJECTED:            { bg: '#FEE2E2', fg: '#B91C1C', label: 'Rejected' },
};

export const RISK_STYLE: Record<RiskClass, { bg: string; fg: string }> = {
  LOW:      { bg: '#DCFCE7', fg: '#0E7C5A' },
  MEDIUM:   { bg: '#E0F2FE', fg: '#075985' },
  HIGH:     { bg: '#FEF3C7', fg: '#92400E' },
  CRITICAL: { bg: '#FEE2E2', fg: '#B91C1C' },
};

export function fmtDate(d: string | null | undefined, locale = 'en'): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(dt);
}
